//@flow
import * as React from 'react';
import classnames from 'classnames';
import Dropzone from 'react-dropzone-v3';
import Papa from 'papaparse';
import _ from 'lodash';
import defaultTheme from './Dropzone.scss';
import { themr } from 'react-css-themr';

type Props = {
    theme: Object,
    disableActiveStyling?: boolean,
    disableRejectStyling?: boolean,
    accept?: string,
    children?: React$Children,
    disableClick?: boolean,
    disablePreview?: boolean,
    preventDropOnDocument?: boolean,
    inputProps?: Object,
    parse?: string,
    multiple?: boolean,
    name?: string,
    maxSize?: number,
    minSize?: number,
    className?: string,
    activeClassName?: string,
    rejectClassName?: string,
    testId?: string,
    style?: Object,
    activeStyle?: Object,
    rejectStyle?: Object,
    onFileUploadStart: ({
        file: File
    }) => void,
    onFileUploadSuccess: ({
        file: File,
        parsedFile: {
            fileRows: Array<any>,
            fileHeaders: Array<string>
        }
    }) => void,
    onFileUploadError: ({
        file: File,
        uploadErrors: Array<string>
    }) => void,
    onClick?: () => void,
    onDrop: () => void,
    onDropAccepted?: () => void,
    onDropRejected?: () => void,
    onDragStart?: () => void,
    onDragEnter?: () => void,
    onDragOver?: () => void,
    onDragLeave?: () => void,
    onFileDialogCancel?: () => void,
    getDropzoneContainer: () => void,
    transformHeader?: string => string
};

export const DropzoneParseEnum = {
    CSV: 'CSV'
};

class DropzoneComponent extends React.PureComponent<Props, void> {
    getDropzoneRef = ref => {
        this.props.getDropzoneContainer?.(ref);
    };
    handleDrop = (...args) => {
        const {
            onDrop,
            parse,
            onFileUploadStart,
            onFileUploadSuccess,
            onFileUploadError,
            transformHeader
        } = this.props;
        if (parse === DropzoneParseEnum.CSV) {
            const [acceptedFiles, rejectedFiles] = args;
            const [file] = acceptedFiles;
            if (file) {
                onFileUploadStart({ file });
                // refactor Use CSVParserUtils
                Papa.parse(file, {
                    header: true,
                    escapeChar: '"',
                    skipEmptyLines: true,
                    transformHeader,
                    complete: results => {
                        const { data, errors, meta } = results;
                        const newData = data.filter(
                            row => !_.every(Object.values(row), _.isEmpty)
                        );
                        if (errors.length === 0) {
                            onFileUploadSuccess({
                                file,
                                parsedFile: {
                                    fileRows: newData,
                                    fileHeaders: meta.fields
                                }
                            });
                        } else {
                            onFileUploadError({
                                file,
                                uploadErrors: errors.map(
                                    error => `${error.code}: ${error.message}`
                                )
                            });
                        }
                    },
                    error: error => {
                        onFileUploadError({
                            file,
                            uploadErrors: [`${error.code}: ${error.message}`]
                        });
                    }
                });
            } else {
                const [rejectedFile] = rejectedFiles;
                onFileUploadError({
                    file,
                    uploadErrors: [
                        `Invalid file${
                            rejectedFile ? `: ${rejectedFile.name}` : ''
                        }`
                    ]
                });
            }
        } else if (onDrop) {
            onDrop(...args);
        }
    };
    render() {
        const {
            theme,
            accept,
            children,
            disableClick,
            disablePreview,
            preventDropOnDocument,
            inputProps,
            multiple,
            name,
            maxSize,
            minSize,
            className,
            activeClassName,
            rejectClassName,
            style,
            activeStyle,
            rejectStyle,
            onClick,
            onDropAccepted,
            onDropRejected,
            onDragStart,
            onDragEnter,
            onDragOver,
            onDragLeave,
            onFileDialogCancel,
            disableActiveStyling,
            disableRejectStyling,
            onDrop, //eslint-disable-line
            getDropzoneContainer, //eslint-disable-line
            ...others
        } = this.props;
        return (
            <Dropzone
                ref={this.getDropzoneRef}
                accept={accept}
                children={children}
                disableClick={disableClick}
                disablePreview={disablePreview}
                preventDropOnDocument={preventDropOnDocument}
                inputProps={inputProps}
                multiple={multiple}
                name={name}
                maxSize={maxSize}
                minSize={minSize}
                className={classnames(className, {
                    [theme.dropzone]: theme.dropzone,
                    [theme.multiple]: theme.multiple && multiple
                })}
                activeClassName={classnames(activeClassName, {
                    [theme.dropzoneActive]:
                        theme.dropzoneActive && !disableActiveStyling
                })}
                rejectClassName={classnames(rejectClassName, {
                    [theme.dropzoneReject]:
                        theme.dropzoneReject && !disableRejectStyling
                })}
                style={style}
                activeStyle={activeStyle}
                rejectStyle={rejectStyle}
                onClick={onClick}
                onDrop={this.handleDrop}
                onDropAccepted={onDropAccepted}
                onDropRejected={onDropRejected}
                onDragStart={onDragStart}
                onDragEnter={onDragEnter}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onFileDialogCancel={onFileDialogCancel}
                {...others}
            />
        );
    }
}
const EnhancedDropzoneComponent = themr(
    'DropzoneComponent',
    defaultTheme
)(DropzoneComponent);

export default EnhancedDropzoneComponent;
export { EnhancedDropzoneComponent as DropzoneComponent };
